<script setup lang="ts">
  import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
  import { Input, Tooltip } from 'ant-design-vue'
  import { computed, ref, watch } from 'vue'
  import { useWindowSize } from '@/composables/useWindowSize'

  type DataType = 'string' | 'number'

  interface IRegexRuleConfigurationProps {
    modelValue?: string | number | null
    required?: boolean
    regex?: string
    dataType?: DataType
    placeHolder?: string
    errorMessage?: string
  }

  /**
   * Component behavior summary:
   * - When dataType === 'string' (default): component behaves like before,
   *   validates input string against `regex` (if provided), emits string.
   * - When dataType === 'number': the component keeps a text input for the UI,
   *   parses the text to a Number. If empty -> does not emits null. If parsed NaN -> invalid.
   *   If a `regex` is provided, it is applied to the raw text the user typed (trimmed).
   *   If validation passes, emits a Number.
   */

  const props = defineProps<IRegexRuleConfigurationProps>()

  const emit = defineEmits<{ 'update:modelValue': [string | number | null] }>()

  const inputValue = ref<string>(props.modelValue == null ? '' : String(props.modelValue))
  const isInvalid = ref(false)
  const compiledRegex = ref<RegExp | undefined>()

  const { controlSize } = useWindowSize()
  const tooltipClass = computed(() => {
    if (controlSize.value === 'small') return 'tooltip-sm'
    if (controlSize.value === 'large') return 'tooltip-lg'
    return 'tooltip-md'
  })

  watch(
    () => props.regex,
    (newPattern) => {
      if (!newPattern) {
        compiledRegex.value = undefined
        return
      }
      try {
        compiledRegex.value = new RegExp(newPattern)
      } catch (e) {
        console.warn(`Invalid regex pattern: ${newPattern}`, e)
        compiledRegex.value = undefined
      }
    },
    { immediate: true }
  )

  // Keep local text in sync with external modelValue changes
  watch(
    () => props.modelValue,
    (newVal) => {
      inputValue.value = newVal == null ? '' : String(newVal)
      // re-validate on external changes (does not re-emit unless needed)
      validateAndEmit(inputValue.value)
    }
  )

  function parseNumberFromString(s: string): number | null | typeof NaN {
    const trimmed = s.trim()
    if (trimmed === '') return null
    // Use Number() to allow decimals and scientific notation; change to parseFloat for different behavior
    const num = Number(trimmed)
    return Number.isFinite(num) ? num : NaN
  }

  /**
   * Validate inputValue and emit appropriate typed modelValue.
   * - For 'number', emit parsed number (or null when empty).
   * - For 'string', emit the raw string.
   *
   * options.emitOnlyIfValid: when true, only emit if validation passes.
   */
  function validateAndEmit(valueStr: string) {
    isInvalid.value = false
    const datatype = props.dataType ?? 'string'

    if (valueStr?.length <= 0) {
      if (props.required === false) {
        isInvalid.value = false
        emit('update:modelValue', null)
        return
      } else {
        isInvalid.value = true
        return
      }
    }

    if (datatype === 'number') {
      const parsed = parseNumberFromString(valueStr)
      if (parsed === null) {
        isInvalid.value = true
        return
      }

      if (Number.isNaN(parsed)) {
        isInvalid.value = true
        return
      }

      // Run regex against the raw input string (trimmed). This lets you write patterns
      // that enforce non-negativity, integer-only, fraction rules, leading zeros, etc.
      if (compiledRegex.value) {
        const target = valueStr.trim()
        if (!compiledRegex.value.test(target)) {
          isInvalid.value = true
          return
        }
      }

      // Passed numeric + regex checks -> emit numeric type
      emit('update:modelValue', parsed)
    } else {
      // string path
      if (compiledRegex.value && valueStr !== '') {
        if (!compiledRegex.value.test(valueStr)) {
          isInvalid.value = true
          return
        }
      }
      emit('update:modelValue', valueStr)
    }
  }

  function handleInput(value: string) {
    inputValue.value = value
    validateAndEmit(value)
  }

  const inputStatus = computed(() => (isInvalid.value ? 'error' : ''))

  const inputAttrs = computed(() => {
    // Use text input with inputmode="numeric" for numeric typing so we allow '-' while typing
    return props.dataType === 'number' ? { type: 'text', inputmode: 'numeric' } : { type: 'text' }
  })
</script>

<template>
  <div>
    <Input
      :value="inputValue"
      v-bind="inputAttrs"
      @update:value="handleInput"
      :placeholder="props.placeHolder || ''"
      :status="inputStatus"
      :size="controlSize"
    >
      <template #suffix>
        <Tooltip
          v-if="isInvalid"
          placement="left"
          :title="props.errorMessage ?? 'Does not match required pattern'"
          :overlayClassName="tooltipClass"
        >
          <ExclamationCircleOutlined style="color: red" />
        </Tooltip>
      </template>
    </Input>
  </div>
</template>

<style scoped>
/* tooltip size variants */
.tooltip-sm .ant-tooltip-inner { font-size:12px; padding:4px 6px; line-height:1; }
.tooltip-md .ant-tooltip-inner { font-size:13px; padding:6px 8px; line-height:1.1; }
.tooltip-lg .ant-tooltip-inner { font-size:14px; padding:8px 10px; line-height:1.2; }
</style>
